import {
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Image,
    Input,
    PlacementWithLogical,
    Text,
    useBoolean,
    useBreakpointValue,
    useMediaQuery,
    useOutsideClick,
} from '@chakra-ui/react'
import {
    useGetEmailVerificationCodeMutation,
    useGetExternalCodeMutation,
    useGetFirstStepVerifyMutation,
} from '@core/app/api/authApi'
import {
    addStepCount,
    allStepsCount,
    authServiceErrorMessage,
    nextStep,
    setEmail,
    setRepeatEmail,
    settings,
    stepCount,
} from '@core/app/slices/auth'
import { useAppDispatch, useAppSelector } from '@core/utils/hooks/hook'
import { Form, Formik } from 'formik'
import { FC, useEffect, useRef, useState } from 'react'
import * as Yup from 'yup'

import { AuthApi } from '@core/app/types/authApiType'
import { AppButton } from '@core/components/AppButton'
import { FieldError } from '@core/components/FieldError/FieldError'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Localization } from '@core/app/types/globalApiType'
import facebookIcon from '@core/assets/icons/socials/facebook.svg'
import googleIcon from '@core/assets/icons/socials/google.svg'
import linkedinIcon from '@core/assets/icons/socials/linkedin.svg'
import microsoftIcon from '@core/assets/icons/socials/microsoft.svg'
import nobankIcon from '@core/assets/icons/socials/nobank.svg'
import sebankIcon from '@core/assets/icons/socials/sebank.svg'
import vippsIcon from '@core/assets/icons/socials/vipps.svg'
import { useAppTheme } from '@core/utils/contexts/ThemeContext/ThemeContext'

interface FormValues {
    email: string
    password: string
}

interface Props {
    isGoogle?: boolean
    isMicrosoft?: boolean
    isLinkedin?: boolean
    isFacebook?: boolean
    isVipps?: boolean
    isNoBankId?: boolean
    isSeBankId?: boolean
    registerSettings?: AuthApi
}

export const CreateProfileStep: FC<Props> = ({
    isFacebook,
    isGoogle,
    isLinkedin,
    isMicrosoft,
    isVipps,
    isNoBankId,
    isSeBankId,
    registerSettings,
}) => {
    const dispatch = useAppDispatch()
    const authSettings = useAppSelector(settings)
    const stepNumber = useAppSelector(stepCount)
    const stepsCount = useAppSelector(allStepsCount)
    const authErrorMessage = useAppSelector(authServiceErrorMessage)
    const [verifyData] = useGetFirstStepVerifyMutation()
    const [getCode] = useGetExternalCodeMutation()
    const [getEmailVerificationCode] = useGetEmailVerificationCodeMutation()
    const initialValues: FormValues = { email: '', password: '' }
    const [isPasswordPopover, setIsPasswordPopover] = useBoolean(false)
    // @ts-ignore
    const placement: PlacementWithLogical = useBreakpointValue({
        base: 'bottom',
        sm: 'bottom',
        md: 'bottom',
        lg: 'bottom',
        xl: 'left',
    })

    const passwordRef = useRef<any>()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { appTheme } = useAppTheme()
    const [localization, setLocalization] = useState<Localization>({})
    const [isMobile] = useMediaQuery('(max-width: 480px)', {
        fallback: false,
    })

    useOutsideClick({
        ref: passwordRef,
        handler: () => setIsPasswordPopover.off(),
    })

    const getCodeHandler = async (serviceId: number) => {
        try {
            const response = await getCode({
                data: {
                    typeId: 2,
                    serviceId,
                },
            }).unwrap()

            location.href = response.link
        } catch (error) {}
    }

    const verifyAuthData = async (email: string, submitted: Function, setStatus: Function) => {
        try {
            await verifyData({
                data: {
                    email,
                },
            }).unwrap()

            dispatch(addStepCount())
            if (authSettings?.verifyEmailBeforeRegistration) {
                getEmailVerificationHandler(email)
                dispatch(nextStep('emailVerification'))
            } else {
                dispatch(nextStep('personalDataExtanded'))
            }
        } catch (error: any) {
            const defaultErrorMsg = 'An unexpected error occurred.'
            let errorMsg = defaultErrorMsg

            if (error.data?.data?.errors?.email) {
                errorMsg = error.data.data.errors.email[0] || defaultErrorMsg
            } else if (error.message) {
                errorMsg = error.message
            }

            setStatus({ email: errorMsg })
        } finally {
            submitted()
        }
    }

    const getEmailVerificationHandler = async (email: string) => {
        try {
            await getEmailVerificationCode({
                data: {
                    email,
                },
            }).unwrap()
        } catch (error) {
            console.log(error)
        }
    }

    const getServiceImage = (serviceId: number) => {
        switch (serviceId) {
            case 1:
                return microsoftIcon
            case 2:
                return googleIcon
            case 10:
                return facebookIcon
            case 11:
                return linkedinIcon
            case 9:
                return vippsIcon
            case 5:
                return sebankIcon
            case 4:
                return nobankIcon
        }
    }

    const getServiceName = (serviceId: number) => {
        switch (serviceId) {
            case 1:
                return 'Microsoft'
            case 2:
                return 'Google'
            case 10:
                return 'Facebook'
            case 11:
                return 'Linkedin'
            case 9:
                return 'Vipps'
            case 5:
                return 'BankID Sweden'
            case 4:
                return 'BankID Norway'
        }
    }

    useEffect(() => {
        if (appTheme && appTheme?.localization) {
            setLocalization(appTheme?.localization)
        }
    }, [appTheme])

    return (
        <Flex flexDirection={'column'}>
            <Text fontSize={'xs'} color={'text.thirdly'} mb={1}>
                {t('6593')} {stepNumber} {t('1165')} {stepsCount}
            </Text>
            <Text fontSize={'2xl'} color={'text.main'} fontWeight={'semibold'} mb={6}>
                {localization[18] ? localization[18] : t('6594')}
            </Text>
            <Flex gap={isMobile ? 4 : 5} alignItems={'center'} flexWrap={'wrap'}>
                {isGoogle && (
                    <Box as={'button'} onClick={() => getCodeHandler(2)} cursor={'pointer'}>
                        <Image src={googleIcon} w={isMobile ? 6 : 8} alt="Google" />
                    </Box>
                )}
                {isMicrosoft && (
                    <Box as={'button'} onClick={() => getCodeHandler(1)} cursor={'pointer'}>
                        <Image src={microsoftIcon} w={isMobile ? 6 : 8} alt="Microsoft" />
                    </Box>
                )}
                {isLinkedin && (
                    <Box as={'button'} onClick={() => getCodeHandler(11)} cursor={'pointer'}>
                        <Image src={linkedinIcon} w={isMobile ? 6 : 8} alt="LinkedIn" />
                    </Box>
                )}
                {isFacebook && (
                    <Box as={'button'} onClick={() => getCodeHandler(10)} cursor={'pointer'}>
                        <Image src={facebookIcon} w={isMobile ? 6 : 8} alt="Facebook" />
                    </Box>
                )}
                {isVipps && (
                    <Box as={'button'} onClick={() => getCodeHandler(9)} cursor={'pointer'}>
                        <Image src={vippsIcon} w={isMobile ? 6 : 8} alt="Vipps" />
                    </Box>
                )}
                {isNoBankId && (
                    <Box as={'button'} onClick={() => getCodeHandler(4)} cursor={'pointer'}>
                        <Image src={nobankIcon} w={isMobile ? 6 : 8} alt="NoBank" />
                    </Box>
                )}
                {isSeBankId && (
                    <Box as={'button'} onClick={() => getCodeHandler(5)} cursor={'pointer'}>
                        <Image src={sebankIcon} w={isMobile ? 6 : 8} alt="SeBank" />
                    </Box>
                )}
            </Flex>
            {(isFacebook || isGoogle || isVipps || isLinkedin || isMicrosoft || isSeBankId || isNoBankId) && (
                <HStack mt={6}>
                    <Divider />
                    <Text fontSize={'xs'} color={'text.thirdly'} textTransform={'lowercase'}>
                        {t('1758')}
                    </Text>
                    <Divider />
                </HStack>
            )}
            {registerSettings?.externalAuth && registerSettings?.externalAuth?.isValid ? (
                <Flex gap={2} alignItems={'center'} mt={3} mb={5}>
                    <Flex
                        w={'40px'}
                        h={'40px'}
                        border={'1px solid'}
                        borderColor={'formElements.borderSuccess'}
                        borderRadius={'full'}
                        justifyContent={'center'}
                        alignItems={'center'}
                    >
                        <Image src={getServiceImage(registerSettings?.externalAuth?.serviceId)} />
                    </Flex>
                    <Text fontSize={'sm'} color={'text.secondary'}>
                        {t('6638')} {getServiceName(registerSettings?.externalAuth?.serviceId)}
                    </Text>
                    <Button
                        variant={'unstyled'}
                        h={'20px'}
                        onClick={() => {
                            localStorage.removeItem('service_code_auth')
                            navigate(
                                {
                                    search: '',
                                },
                                { replace: true }
                            )
                        }}
                    >
                        <Text fontSize={'sm'} fontWeight={'medium'} color={'text.link'}>
                            {t('6639')}
                        </Text>
                    </Button>
                </Flex>
            ) : (
                <Box as={'div'} my={6} gap={0}>
                    <Text fontWeight={'semibold'} color={'text.main'}>
                        {localization[19] ? localization[19] : t('6595')}
                    </Text>
                    <Text fontSize={'sm'} color={'text.secondary'}>
                        {t('6596')}?
                        <Button
                            variant={'link'}
                            fontSize={'sm'}
                            color={'text.link'}
                            fontWeight={'normal'}
                            ml={1}
                            onClick={() => (location.href = '/login')}
                        >
                            {t('1890')}
                        </Button>
                    </Text>
                </Box>
            )}
            <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={Yup.object({
                    email: Yup.string()
                        .required(`${t('6456')}`)
                        .email(`${t('6598')}`),
                    // password: Yup.string()
                    //     .required(`${t('6599')}`)
                    //     .matches(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?!.* )(?=.*[^a-zA-Z0-9]).{8,64}$/m, `${t('6600')}`),
                })}
                onSubmit={(values, { setSubmitting, setStatus }) => {
                    const submitted = () => {
                        setSubmitting(false)
                    }
                    dispatch(setEmail(values.email))
                    dispatch(setRepeatEmail(values.email))
                    verifyAuthData(values.email, submitted, setStatus)
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                    isSubmitting,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    status,
                    setStatus,
                    setFieldValue,
                }) => (
                    <Form onSubmit={handleSubmit} style={{ marginBottom: '0px' }}>
                        <FormControl mb={4} isInvalid={(!!errors.email && touched.email) || (status && !!status.email)}>
                            <FormLabel>
                                <Text fontSize={'xs'}>{t('92')}*</Text>
                            </FormLabel>
                            <Input
                                type={'email'}
                                name={'email'}
                                value={values.email}
                                onChange={(e) => {
                                    handleChange(e)
                                    setStatus({
                                        email: '',
                                    })
                                }}
                                placeholder={`${t('727')}`}
                            />
                            {status && status.email && (
                                <Box mt={2}>
                                    <FieldError text={status.email} />
                                </Box>
                            )}
                            <FormErrorMessage>{errors.email && touched.email && errors.email}</FormErrorMessage>
                        </FormControl>
                        {authErrorMessage && !values.email ? <FieldError text={authErrorMessage} /> : null}
                        <FormControl mt={6} display={'flex'} justifyContent={'end'}>
                            <AppButton
                                translate="no"
                                title={t('3035')}
                                type={'submit'}
                                isLoading={isSubmitting}
                                variant={'primary'}
                                w={'120px'}
                                h={'44px'}
                            />
                        </FormControl>
                    </Form>
                )}
            </Formik>
        </Flex>
    )
}
